import { useState, useEffect } from 'react';
import { usePipeline, useStages } from './hooks/usePipeline';
import { useTabRouter, resolveVisibleTab, type TabKey } from './hooks/useTabRouter';
import { ChromelessContext } from './hooks/useChromeless';
import { useAuthUser } from './auth/useAuthUser';
import { Header } from './components/layout/Header';
import { ImpersonationBanner } from './components/layout/ImpersonationBanner';
import { RosterAuditBanner } from './components/layout/RosterAuditBanner';
import { TabBar } from './components/layout/TabBar';
import { OverviewPage } from './components/pages/OverviewPage';
import { StaffingPage } from './components/pages/StaffingPage';
import { IntelPage } from './components/intel/IntelPage';
import { AdminPage } from './components/pages/AdminPage';
import { PromptsPage } from './components/pages/PromptsPage';
import { PipelineInsightsPage } from './components/pages/PipelineInsightsPage';
import { PipelineV2Page } from './components/pages/PipelineV2Page';
import { OutreachPage } from './components/pages/OutreachPage';
import { VocabulariesPage } from './components/pages/VocabulariesPage';
import { SectorCoveragePage } from './components/pages/SectorCoveragePage';
import { DealsPage } from './components/pages/DealsPage';
import { NdaPage } from './components/pages/NdaPage';
import { CrmPage } from './components/pages/CrmPage';
import { AllDealsPage } from './components/pages/AllDealsPage';
import { SettingsPage } from './components/pages/SettingsPage';
import { useMyTabs } from './hooks/useAdmin';

/**
 * The tabs still rendered from the Affinity-fed `opportunities` snapshot (`/api/pipeline`).
 * Reference-only surfaces: the snapshot's producer was switched off at the 2026-08-08 cut, so
 * they are frozen at their last refresh. Everything NOT in this set — the promoted native
 * Pipeline board first among them — must render without waiting on that fetch, which is why the
 * loading/error gate below is scoped instead of blocking the whole app.
 *
 * ⛔ `overview` LEFT this set on 2026-09-01 (post-Affinity S2) and must not come back. The Overview
 * tab is served entirely by `/api/analytics/summary`, which is now native end to end — including the
 * Fund II bar's deals, which used to arrive here as props off this fetch. Re-adding it would make
 * the page wait on a frozen snapshot it does not read.
 *
 * ⛔ `pipeline-v1`, `pipeline-by-sector` and `agenda` left it on 2026-09-01 by being DELETED
 * (post-Affinity S1, the retirement wave). `intel` is deliberately the LAST member: it stays
 * frozen by owner ruling, and it is now the only consumer of `/api/pipeline` in the whole app —
 * which is precisely why that endpoint and `pipeline_builder` survive the wave.
 */
const SNAPSHOT_TABS: ReadonlySet<TabKey> = new Set<TabKey>(['intel']);

export default function App() {
  const authUser = useAuthUser();
  const myTabs = useMyTabs();
  // Past the visibility gate below, `myTabs.data` is present unless `/api/tabs` errored — so
  // the permissive branch is now the error path only, not a loading state.
  const canSeeTab = (key: string) =>
    myTabs.data ? myTabs.data.visible.includes(key) : true;
  useStages(); // Load stage order from API on mount

  // `urlTab` is what the URL asks for; `activeTab` (below, after the visibility gate) is what
  // we actually render. They differ only when the caller can't see the tab they landed on.
  const [urlTab, setActiveTab] = useTabRouter();

  // Resolved HERE, above the visibility gate's early return, only so the snapshot fetch can be
  // gated on it — `activeTab` below is this same value, and the two must never diverge.
  const resolvedTab = resolveVisibleTab(urlTab, myTabs.data?.visible);

  // ⚠ GATED, not just non-blocking. The scoped loading gate below (`needsSnapshot`) stops the
  // reference-only tab from holding first paint for everyone else — but this call was once ISSUED
  // on every page, so the promoted native board, the CRM grid and every other surface each fired a
  // full Affinity-snapshot read they never render. Paired with the 10s poll that used to live in
  // `usePipeline` (deleted — see that hook's docstring for why it could never clear), one open tab
  // meant six `/api/pipeline` reads a minute, forever, on a single-worker app. Gate it on the same
  // set the render gate uses, so the fetch and the need for it cannot drift apart.
  const { data, isLoading, error } = usePipeline(SNAPSHOT_TABS.has(resolvedTab));

  // Landing-page safety net, new with the promotion. Before it, DEFAULT_TAB was an
  // 'everyone' tab, so "the resolved tab isn't one I'm allowed to see" could only happen by
  // typing a URL. The default is now the native board, whose bootstrap min_role starts
  // closed in an unconfigured environment. This effect only corrects the URL to match what
  // we render (replace, so Back doesn't bounce straight into the tab they can't see) — the
  // RENDER decision is made synchronously by `resolveVisibleTab`, because an effect lands a
  // frame late and that frame is exactly the flash this is meant to avoid.
  useEffect(() => {
    const resolved = resolveVisibleTab(urlTab, myTabs.data?.visible);
    if (resolved !== urlTab) setActiveTab(resolved, { replace: true });
  }, [myTabs.data, urlTab, setActiveTab]);
  // full-viewport pages (the CRM list grid) hide the big header via this setter (useChromeless).
  // The matching #root full-viewport class (min-height:0 + no bottom padding) is applied SYNCHRONOUSLY
  // inside useChromeless's layout effect — not here — so it's in effect for the grid's pre-paint fill
  // measurement (a passive effect here would apply it after first paint → a squish-then-expand jump).
  const [chromeless, setChromeless] = useState(false);

  // NOTHING renders until `/api/tabs` answers. The chrome used to paint first and correct
  // itself: `canSeeTab` returns true while the list is unresolved and the TabBar falls back
  // to its full static list, so every caller briefly saw a bar containing tabs they are not
  // entitled to — and a caller whose landing tab is closed to them saw a header over an
  // empty body. Visibility is cached for 5 minutes (`useMyTabs`), so this is one short wait
  // on first load per session, not per navigation.
  //
  // Deliberately `isPending`, not "no data": if `/api/tabs` ERRORS we fall through and render
  // optimistically, exactly as before. Every page and endpoint is independently gated on the
  // server, so a transient failure of this one call must not white-screen the dashboard.
  if (myTabs.isPending) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999', fontFamily: "'DM Mono', monospace" }}>
        Loading…
      </div>
    );
  }

  // The tab actually rendered — see `resolveVisibleTab`. The effect above separately corrects
  // the URL; this keeps the first painted frame correct without waiting for it. Computed above
  // the gate (as `resolvedTab`) because the snapshot fetch is gated on the same value.
  const activeTab = resolvedTab;

  // The snapshot gate is SCOPED to the tabs that read it (SNAPSHOT_TABS). It used to be
  // unconditional, which meant every surface — including the native board, which shares no
  // data with `/api/pipeline` — held first paint until the Affinity-fed snapshot landed.
  // Now `/` renders as soon as its own queries do, and Intel keeps the same loading/error
  // copy it always had.
  const needsSnapshot = SNAPSHOT_TABS.has(activeTab);

  if (needsSnapshot && isLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999', fontFamily: "'DM Mono', monospace" }}>
        Loading pipeline data...
      </div>
    );
  }

  if (needsSnapshot && (error || !data)) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#C62828', fontFamily: "'DM Mono', monospace" }}>
        {error ? `Error loading pipeline: ${error.message}` : 'No data available'}
      </div>
    );
  }

  return (
    <ChromelessContext.Provider value={setChromeless}>
      <ImpersonationBanner />
      <RosterAuditBanner />
      {!chromeless && (
        <Header userName={authUser.name} />
      )}
      <TabBar activeTab={activeTab} onTabChange={setActiveTab} />
      {/* No `data &&` guard: Overview reads its own native endpoint and never the snapshot. */}
      {activeTab === 'overview' && <OverviewPage />}
      {activeTab === 'staffing' && (
        <StaffingPage />
      )}
      {/* The last reader of the frozen snapshot — frozen by owner ruling, banner and all. */}
      {activeTab === 'intel' && data && (
        <IntelPage sectors={data.sectors} />
      )}
      {activeTab === 'sector-coverage' && canSeeTab('sector-coverage') && (
        <SectorCoveragePage />
      )}
      {activeTab === 'deals' && canSeeTab('deals') && (
        <DealsPage />
      )}
      {/* DASH-2v — the native board. Promoted to the DEFAULT tab on 2026-08-11, so this is
          what `/` serves; the Affinity-fed board it replaced was deleted on 2026-09-01 by the
          post-Affinity retirement wave. Still gated: `canSeeTab` keeps a closed environment
          closed, and the visible-tab fallback effect above stops a caller who cannot see it
          landing on a blank page. */}
      {activeTab === 'pipeline' && canSeeTab('pipeline') && (
        <PipelineV2Page />
      )}
      {activeTab === 'prompts' && canSeeTab('prompts') && (
        <PromptsPage />
      )}
      {activeTab === 'pipeline-insights' && canSeeTab('pipeline-insights') && (
        <PipelineInsightsPage />
      )}
      {activeTab === 'outreach' && canSeeTab('outreach') && (
        <OutreachPage />
      )}
      {activeTab === 'vocabularies' && canSeeTab('vocabularies') && (
        <VocabulariesPage />
      )}
      {/* The All Deals shortcut: the CRM surface, seeded onto the deal-pipeline list (see
          AllDealsPage). Gated on its own key, which /api/tabs grants exactly when it grants 'crm'. */}
      {activeTab === 'all-deals' && canSeeTab('all-deals') && (
        <AllDealsPage />
      )}
      {activeTab === 'crm' && canSeeTab('crm') && (
        <CrmPage />
      )}
      {activeTab === 'nda' && canSeeTab('nda') && (
        <NdaPage />
      )}
      {activeTab === 'settings' && (
        // 'everyone' tab (self-scoped /me data only) — rendered without a
        // canSeeTab gate, matching the other 'everyone' tabs (overview/pipeline).
        <SettingsPage />
      )}
      {activeTab === 'admin' && canSeeTab('admin') && (
        <AdminPage />
      )}
    </ChromelessContext.Provider>
  );
}
